import React from 'react';
import { Flex } from '@withjoy/joykit';
import { useSortableItemContext } from '@shared/components/SortableList/context/SortableItem.context';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { DragIcon } from '@assets/icons';
import { DragHandle } from '../SortableCard/SortableCard';
import { StayZoneTile } from '@shared/components/StayZoneTile/StayZoneTile';
import type { StayZoneAreaData } from '@shared/components/StayZoneTile/StayZoneTile.types';
import { DistanceFromVenue } from '@shared/components/DistanceFromVenue';
import { DistanceStatistics } from '@shared/components/DistanceStatistics';
import { pxToRem } from '@withjoy/joykit/theme';
import { StatusLabel } from '@shared/components/StatusLabel';

interface SortableStayZoneTileProps
  extends Readonly<{
    stayZone: StayZoneAreaData;
    isDragActive: boolean;
    onView: (stayZone: StayZoneAreaData) => void;
    onRemove: (stayZone: StayZoneAreaData) => void;
    containerWidth?: number | null;
    isItemDragActive?: boolean;
  }> {}

export const SortableStayZoneTile = (props: SortableStayZoneTileProps) => {
  const { stayZone, isDragActive, onView, onRemove, containerWidth, isItemDragActive } = props;

  const isMobile = useIsMobileScreen();
  const { attributes, listeners, draggableNodeRef } = useSortableItemContext();
  const firstImage = stayZone.carouselPhotos.at(0);

  // @todo [TRAV-2250] Enable drag-and-drop when backend supports reordering
  const isSortableListEnabled = false;

  return (
    <StayZoneTile
      role="group"
      cursor="default"
      paddingX={4}
      paddingTop={4}
      paddingBottom={6}
      minWidth="fit-content"
      width={containerWidth ? pxToRem(containerWidth) : '100%'}
    >
      {isSortableListEnabled && (
        <DragHandle
          visibility={isDragActive || isMobile ? 'visible' : 'hidden'}
          cursor={isDragActive ? 'grabbing' : 'grab'}
          {...attributes}
          {...listeners}
          ref={draggableNodeRef}
        >
          <DragIcon
            size={16}
            style={{ transform: 'rotate(90deg)' }}
          />
        </DragHandle>
      )}
      <StayZoneTile.Header
        menu={
          <StayZoneTile.Menu
            stayZone={stayZone}
            onView={onView}
            onRemove={onRemove}
          />
        }
        distance={
          <Flex
            gap={3}
            alignItems="center"
          >
            <DistanceFromVenue
              distances={stayZone.travelDistances}
              hasIcon
              size="sm"
            />
            <DistanceStatistics
              distances={stayZone.travelDistances}
              fontSize={pxToRem(13)}
            />
          </Flex>
        }
      >
        {stayZone.name}
      </StayZoneTile.Header>

      {!isItemDragActive && (
        <Flex
          flexDirection="column"
          marginTop={3}
          paddingX={4}
          gap={5}
        >
          {firstImage && (
            <StayZoneTile.Photo
              photoUrl={firstImage.src}
              name={stayZone.name}
              borderRadius={pxToRem(12)}
            >
              {stayZone.bestForLabel && (
                <StatusLabel
                  top={5}
                  right={5}
                  gap={2}
                  position="absolute"
                >
                  <span aria-hidden={true}>{stayZone.bestForLabel.emoji}</span>
                  {stayZone.bestForLabel.label}
                </StatusLabel>
              )}
            </StayZoneTile.Photo>
          )}
          {stayZone.features.length > 0 && (
            <Flex flexDirection="column">
              {stayZone.features.map(feature => (
                <StayZoneTile.Feature
                  key={`${stayZone.id}-feature-${feature}`}
                  feature={feature}
                />
              ))}
            </Flex>
          )}
        </Flex>
      )}
    </StayZoneTile>
  );
};
